import type { HTMLAttributes, ReactNode } from 'react'
import { DisplayHeading } from '../display-heading/DisplayHeading.tsx'
import type { DisplayHeadingSize } from '../display-heading/DisplayHeading.tsx'
import styles from './SpotlightPanel.module.css'

export interface SpotlightFigure {
  /** The number itself: "4–1", "3rd", "6". */
  value: ReactNode
  /** What it counts, read under it: "Lakewood League XIV", "Players". */
  label: string
}

export interface SpotlightPanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /**
   * The org's own colour, as a CSS colour. Drawn as a 2px rule beneath the
   * logo tile and nowhere else — never as a background wash. Carousel hosts
   * white-label brands, so the frame stays neutral and lets the org's mark be
   * the one coloured thing on the panel.
   */
  brandColor?: string
  /** Logo image or initial tile. Sized by the panel. */
  logo?: ReactNode
  /** Small line above the title — org name, division, region. */
  eyebrow?: ReactNode
  title: ReactNode
  titleSize?: DisplayHeadingSize
  /** Heading level. h3 when the panel sits under a section's own h2. */
  titleAs?: 'h2' | 'h3'
  /** Facts rendered on one line, separated by hairline slashes. */
  meta?: ReactNode[]
  /**
   * Headline numbers, set large under the meta line — a team's record, its
   * standing. For the two or three figures someone came to read; anything
   * smaller belongs in `meta`.
   */
  figures?: SpotlightFigure[]
  /** Body of the panel, under the meta line — a FillMeter, typically. */
  children?: ReactNode
  /** Right-hand rail: countdown, prize, eligibility. */
  aside?: ReactNode
  /** Buttons, pinned to the bottom of the rail. */
  actions?: ReactNode
}

/**
 * One item given the stage above a list.
 *
 * An even grid of twenty-four cards says "results". A stage says "this one,
 * closing Friday" — which is the whole difference between a directory and a
 * product. Used for the soonest-closing competition and the featured org.
 */
export function SpotlightPanel({
  brandColor,
  logo,
  eyebrow,
  title,
  titleSize = '5xl',
  titleAs = 'h2',
  meta,
  figures,
  children,
  aside,
  actions,
  className,
  ...props
}: SpotlightPanelProps) {
  const classes = [styles.panel, className].filter(Boolean).join(' ')
  const hasRail = Boolean(aside ?? actions)

  return (
    <section className={classes} {...props}>
      <div className={styles.main}>
        {(logo ?? eyebrow) && (
          <div className={styles.head}>
            {logo && (
              <div className={styles.logoWrap}>
                <div className={styles.logo}>{logo}</div>
                {brandColor && (
                  <span
                    className={styles.brandRule}
                    style={{ background: brandColor }}
                    aria-hidden="true"
                  />
                )}
              </div>
            )}
            {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
          </div>
        )}

        <DisplayHeading as={titleAs} size={titleSize} uppercase={false} className={styles.title}>
          {title}
        </DisplayHeading>

        {meta && meta.length > 0 && (
          <div className={styles.meta}>
            {meta.map((item, i) => (
              // Meta entries are positional facts with no stable id of their
              // own; the list is fixed per render and never reordered.
              <span key={i} className={styles.metaItem}>
                {i > 0 && <span className={styles.metaSep} aria-hidden="true">/</span>}
                {item}
              </span>
            ))}
          </div>
        )}

        {figures && figures.length > 0 && (
          <dl className={styles.figures}>
            {figures.map((f) => (
              <div key={f.label} className={styles.figure}>
                <dt className={styles.figureLabel}>{f.label}</dt>
                <dd className={styles.figureValue}>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {children}
      </div>

      {hasRail && (
        <div className={styles.rail}>
          {aside}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
    </section>
  )
}
